import { useEffect, useRef, useState } from 'react';
import { HIGHLIGHT_ODDS_CHANGE_TIME } from 'config/constants';

/**
 * Keeps a price's up/down mark for the full highlight time.
 *
 * Live events arrive one by one, and each arrival replaces the "previous"
 * snapshot the change is measured against, so the raw label went blank as soon
 * as another match of the sport came in and cut the animation short. The mark
 * is taken when the price itself changes and dropped only by its own timer;
 * `key` changes with every new mark so a price that moves again flashes again.
 */
export function useHeldOddChange(
  label: string | false | null | undefined,
  value: number | undefined
): { label: string; key: number } {
  const [held, setHeld] = useState<{ label: string; at: number } | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((t) => window.clearTimeout(t));
  }, []);

  useEffect(() => {
    if (!label) return;
    const at = Date.now();
    setHeld({ label, at });
    timers.current.push(
      window.setTimeout(
        () => setHeld((h) => (h && h.at === at ? null : h)),
        HIGHLIGHT_ODDS_CHANGE_TIME
      )
    );
    // Only a change of this price starts a mark; the label is read with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return { label: held?.label ?? '', key: held?.at ?? 0 };
}
